import React, { useState } from 'react';
import { ChevronRight, ChevronDown, ArrowRight, ExternalLink } from 'lucide-react';
import type { Issue, AffectedRow } from './types';
import IssueStatusBadge from './IssueStatusBadge';
import SeverityBadge from './SeverityBadge';

interface IssuesTableProps {
  issues: Issue[];
  onNavigateToQuestion: (questionId: string) => void;
  onNavigateToDataPreview: (issueId: string) => void;
}

const PAGE_SIZE = 10;

const IssuesTable: React.FC<IssuesTableProps> = ({ issues, onNavigateToQuestion, onNavigateToDataPreview }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rowPages, setRowPages] = useState<Record<string, number>>({});

  if (!issues || issues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-[#D1FAE5] flex items-center justify-center mb-3">
          <span className="text-lg">&#10003;</span>
        </div>
        <p className="text-sm font-semibold text-[#0F172A]">No issues found</p>
        <p className="text-xs text-[#94A3B8] mt-1">Your data passed all validation checks.</p>
      </div>
    );
  }

  const toggleRow = (issueId: string) => {
    setExpandedId(prev => prev === issueId ? null : issueId);
  };

  const getPage = (issueId: string) => rowPages[issueId] || 0;
  const setPage = (issueId: string, page: number) => setRowPages(prev => ({ ...prev, [issueId]: page }));

  return (
    <div className="border border-[#E2E8F0] rounded-xl overflow-x-auto">
      <table className="w-full min-w-[860px] text-[13px]">
        <thead>
          <tr className="bg-[#1E293B] text-white">
            <th className="px-4 py-3 text-left font-semibold text-[11px] uppercase tracking-wider">Severity</th>
            <th className="px-4 py-3 text-left font-semibold text-[11px] uppercase tracking-wider">Type</th>
            <th className="px-4 py-3 text-left font-semibold text-[11px] uppercase tracking-wider">Field</th>
            <th className="px-4 py-3 text-left font-semibold text-[11px] uppercase tracking-wider">Business Impact</th>
            <th className="px-4 py-3 text-center font-semibold text-[11px] uppercase tracking-wider">Records</th>
            <th className="px-4 py-3 text-center font-semibold text-[11px] uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 w-10"></th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue, i) => {
            const isExpanded = expandedId === issue.issue_id;
            return (
              <React.Fragment key={issue.issue_id}>
                <tr
                  onClick={() => toggleRow(issue.issue_id)}
                  className={`cursor-pointer transition-colors ${
                    isExpanded ? 'bg-[#FAFCFF]' : i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'
                  } hover:bg-[#F0FDFA]`}
                >
                  <td className="px-4 py-3"><SeverityBadge severity={issue.severity} /></td>
                  <td className="px-4 py-3 font-medium text-[#0F172A]">{formatType(issue.type)}</td>
                  <td className="px-4 py-3"><code className="text-[12px] bg-[#F1F5F9] px-1.5 py-0.5 rounded text-[#334155] font-mono">{issue.column}</code></td>
                  <td className="px-4 py-3 text-[#0F172A] max-w-[300px]">
                    <span className="line-clamp-2">{issue.business_explanation}</span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-[#0F172A]">{issue.affected_count}</td>
                  <td className="px-4 py-3 text-center"><IssueStatusBadge status={issue.status} /></td>
                  <td className="px-4 py-3 text-center">
                    {isExpanded
                      ? <ChevronDown className="w-4 h-4 text-[#64748B]" />
                      : <ChevronRight className="w-4 h-4 text-[#64748B]" />
                    }
                  </td>
                </tr>

                {isExpanded && (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <div className="bg-[#FAFCFF] border-t border-[#E2E8F0] px-6 py-5 animate-in fade-in duration-200">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                          <WhatWeFound issue={issue} />
                          <WhatJarvisDid issue={issue} onNavigateToQuestion={onNavigateToQuestion} />
                        </div>
                        <AffectedRecords
                          issue={issue}
                          page={getPage(issue.issue_id)}
                          onPageChange={(p) => setPage(issue.issue_id, p)}
                          onNavigateToDataPreview={onNavigateToDataPreview}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function formatType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const WhatWeFound: React.FC<{ issue: Issue }> = ({ issue }) => (
  <div>
    <h4 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-3">What We Found</h4>

    {issue.value_distribution && issue.value_distribution.length > 0 && (
      <div className="mb-3">
        <p className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide mb-2">Variants Found</p>
        <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[#F1F5F9]">
                <th className="px-3 py-1.5 text-left font-semibold text-[#64748B] text-[10px] uppercase">Value</th>
                <th className="px-3 py-1.5 text-right font-semibold text-[#64748B] text-[10px] uppercase">Count</th>
              </tr>
            </thead>
            <tbody>
              {issue.value_distribution.map((v, i) => {
                const maxCount = Math.max(...issue.value_distribution!.map(d => d.count));
                const barWidth = maxCount > 0 ? (v.count / maxCount) * 100 : 0;
                return (
                  <tr key={i} className="border-t border-[#F1F5F9]">
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <code className="text-[11px] font-mono text-[#334155]">{v.value}</code>
                        <div className="flex-1 h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                          <div className="h-full bg-[#0D9488] rounded-full" style={{ width: `${barWidth}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right font-semibold text-[#0F172A]">{v.count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {(!issue.value_distribution || issue.value_distribution.length === 0) && (
      <p className="text-[13px] text-[#0F172A] mb-3">
        {issue.affected_count} records {issue.type === 'NULL' ? 'are missing a value' : 'affected'} in <code className="font-mono text-[12px] bg-[#F1F5F9] px-1 rounded">{issue.column}</code>
      </p>
    )}

    <div className="bg-[#FEF3C7] border-l-[3px] border-[#F59E0B] rounded-r-lg px-3 py-2.5">
      <p className="text-[12px] text-[#92400E] leading-relaxed">{issue.business_explanation}</p>
    </div>
  </div>
);

const STATUS_PANEL: Record<string, { bg: string; border: string; textColor: string }> = {
  AUTO_FIXED:        { bg: 'bg-[#D1FAE5]', border: 'border-[#A7F3D0]', textColor: 'text-[#065F46]' },
  AWAITING_DECISION: { bg: 'bg-[#FEF3C7]', border: 'border-[#FDE68A]', textColor: 'text-[#92400E]' },
  FLAGGED:           { bg: 'bg-[#DBEAFE]', border: 'border-[#BFDBFE]', textColor: 'text-[#1E40AF]' },
  CANNOT_RESOLVE:    { bg: 'bg-[#FEE2E2]', border: 'border-[#FECACA]', textColor: 'text-[#991B1B]' },
};

const WhatJarvisDid: React.FC<{ issue: Issue; onNavigateToQuestion: (id: string) => void }> = ({ issue, onNavigateToQuestion }) => {
  const panel = STATUS_PANEL[issue.status] || STATUS_PANEL.FLAGGED;

  return (
    <div>
      <h4 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-3">What Jarvis Did</h4>
      <div className={`${panel.bg} border ${panel.border} rounded-lg px-4 py-3`}>
        {issue.status === 'AUTO_FIXED' && (
          <>
            <p className={`text-[13px] ${panel.textColor} font-semibold mb-2`}>{'\u2705'} Auto-Corrected</p>
            <p className={`text-[13px] ${panel.textColor} mb-2`}>{issue.action_taken}</p>
            {issue.action_reason && <p className={`text-[12px] ${panel.textColor} opacity-80 mb-2`}>{issue.action_reason}</p>}
            <p className={`text-[11px] ${panel.textColor} opacity-70 italic`}>Original values are preserved in your audit log.</p>
          </>
        )}
        {issue.status === 'AWAITING_DECISION' && (
          <>
            <p className={`text-[13px] ${panel.textColor} font-semibold mb-2`}>{'\u26A0\uFE0F'} No Automatic Change Applied</p>
            <p className={`text-[13px] ${panel.textColor} mb-3`}>{issue.action_reason}</p>
            {issue.linked_question_id && (
              <button
                onClick={(e) => { e.stopPropagation(); onNavigateToQuestion(issue.linked_question_id!); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#FDE68A] rounded-lg text-[12px] font-semibold text-[#92400E] hover:bg-[#FFFBEB] transition-colors"
              >
                Answer Question #{issue.linked_question_id} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </>
        )}
        {issue.status === 'FLAGGED' && (
          <>
            <p className={`text-[13px] ${panel.textColor} font-semibold mb-2`}>{'\uD83D\uDD35'} Flagged for Awareness</p>
            <p className={`text-[13px] ${panel.textColor}`}>{issue.action_reason}</p>
          </>
        )}
        {issue.status === 'CANNOT_RESOLVE' && (
          <>
            <p className={`text-[13px] ${panel.textColor} font-semibold mb-2`}>{'\uD83D\uDD34'} Cannot Auto-Resolve</p>
            <p className={`text-[13px] ${panel.textColor} mb-2`}>{issue.action_reason}</p>
            <p className={`text-[11px] ${panel.textColor} opacity-70 italic`}>This requires a correction to your source data before re-upload.</p>
          </>
        )}
      </div>
    </div>
  );
};

const AffectedRecords: React.FC<{
  issue: Issue;
  page: number;
  onPageChange: (page: number) => void;
  onNavigateToDataPreview: (issueId: string) => void;
}> = ({ issue, page, onPageChange, onNavigateToDataPreview }) => {
  const rows = issue.affected_rows || [];
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (rows.length === 0) return null;

  return (
    <div>
      <h4 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-3">
        Affected Records <span className="font-normal text-[#94A3B8]">({issue.affected_count} total)</span>
      </h4>
      <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-[#F1F5F9]">
              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Row #</th>
              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Field</th>
              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Original Value</th>
              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Current Value</th>
              <th className="px-3 py-2 text-center font-semibold text-[#64748B] text-[10px] uppercase">Changed</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr key={i} className="border-t border-[#F1F5F9]">
                <td className="px-3 py-2 font-mono text-[#64748B]">{row.original_row_number}</td>
                <td className="px-3 py-2 font-mono text-[#334155]">{row.field}</td>
                <td className="px-3 py-2 font-mono text-[#64748B]">{row.original_value || <span className="italic text-[#94A3B8]">NULL</span>}</td>
                <td className={`px-3 py-2 font-mono ${row.changed ? 'bg-[#DCFCE7] text-[#047857] font-semibold border-l-2 border-[#A7F3D0]' : 'text-[#64748B]'}`}>
                  {row.current_value || <span className="italic text-[#94A3B8]">NULL</span>}
                  {row.changed && <span className="ml-2 text-[9px] bg-[#A7F3D0] text-[#065F46] px-1 py-0.5 rounded font-bold uppercase">edited</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {row.changed
                    ? <span className="inline-flex items-center px-1.5 py-0.5 bg-[#D1FAE5] text-[#065F46] text-[10px] font-bold rounded">Changed</span>
                    : <span className="inline-flex items-center px-1.5 py-0.5 bg-[#F1F5F9] text-[#94A3B8] text-[10px] font-medium rounded">No change</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex gap-1">
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); onPageChange(i); }}
              className={`w-7 h-7 rounded text-[11px] font-semibold transition-colors ${
                page === i ? 'bg-[#0D9488] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
              }`}
            >
              {i + 1}
            </button>
          ))}
          {totalPages > 5 && <span className="text-[11px] text-[#94A3B8] px-1 self-center">...</span>}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onNavigateToDataPreview(issue.issue_id); }}
          className="text-[12px] font-semibold text-[#0D9488] hover:text-[#0B7A70] flex items-center gap-1"
        >
          View all {issue.affected_count} records in Data Preview <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default IssuesTable;
